import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { F1120lSchAService } from './f1120l-sch-a.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120l-sch-a',
    templateUrl: './f1120l-sch-a.component.html',
    styleUrls: ['./f1120l-sch-a.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120lSchAComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Diagnostic";
  minimise = false;
  isSaveClicked = false;

  // Modal properties
  modalTitle = "Schedule A Dividends, Inclusions, Dividends-Recieved Deduction, and Other Special Deductions";

  // Data properties
  showFormLoader = true;
  showDiagLoader = true;
  scheduleAFormView: any = { rows: [] };
  formData: any[] = [];
  accountData: any[] = [];
  totalLines: any[] = [];
  main_sub_data: any[] = [];
  acct_data: any[] = [];
  acctChild: any[] = [];
  
  // Flags
  no_acct_child = false;
  no_main_sub_data = false;
  show_toggle = true;
  show_toggle_total = true;
  no_total_lines = false;

  // Entity properties
  leId: string = '';
  formName: string = '1120L';
  cdr_no: string = '';
  leType: string = '';
  systemFlag: string = 'D';

  // Tabs configuration
  tabs: any[] = [
    { id: 1, name: 'Diagnostic' },
    { id: 2, name: 'Form View' }
  ];
  activeTab: number = 1;

  // Input properties for integration
  rowData: any = {};
  colData: any = {};  // Column data from grid (contains: field_name, header_name, editable, etc.)
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  screen_key: any;

  // Tracking indices for drill-down
  currType: string = '';
  main_indx: any = 0;
  main_sub_indx: any;
  acct_indx: any;

  constructor(
    private f1120lSchAService: F1120lSchAService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.colData = activeScreen?.data?.colData || {};
    if (this.colData && Object.keys(this.colData).length > 0) {
      this.setDefaultTab();
    }
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Setup basic entity properties
        this.leId = this.rowData.LEID;
        this.cdr_no = this.rowData.MARS;
        this.leType = this.rowData.LE_TYPE;
        
        this.screen_key = this.filterParams.screen_key || this.globalService?.globalParams?.screen_key;
        
        // Load initial data
        this.fetchSchdFormView();
        this.getAccts("parent", false, 1, "", 0, this.rowData.LINE_NO, 0);
      }
    } catch (e) {
      console.error('Error in F1120L-SCH-A init:', e);
    }
  }

  /**
   * Set default tab based on which column opened the modal
   * - If opened from "Diagnostic" column -> open tab 1 (Diagnostic)
   * - If opened from "Form View" column -> open tab 2 (Form View)
   */
  private setDefaultTab() {
    if (!this.colData || Object.keys(this.colData).length === 0) {
      return;
    }

    // colData has 'label' property with column name
    const label = (this.colData.label || '').toLowerCase();
    const fieldName = (this.colData.field_name || '').toLowerCase();
    const headerName = (this.colData.header_name || '').toLowerCase();
    
    const columnName = label || fieldName || headerName;
    console.log("Setting default tab based on column:", columnName);

    // Check if column name contains "diagnostic"
    if (columnName.includes('diagnostic')) {
      this.activeTab = 1;
      console.log("Opening Diagnostic tab (tab 1)");
    }
    // Check if column name contains "form view" or "form_view"
    else if (columnName.includes('form') && (columnName.includes('view') || columnName.includes('_view') || columnName.includes(' view'))) {
      this.activeTab = 2;
      console.log("Opening Form View tab (tab 2)");
    }
    // Default to Diagnostic tab if no match
    else {
      this.activeTab = 1;
      console.log("No matching column name, defaulting to Diagnostic tab (tab 1)");
    }
  }

  fetchSchdFormView() {
    const params = {
      "action_id": 1245,
      "screen_key": this.screen_key,
      "tax_year": this.rowData.TAX_YEAR,
      "form_no": this.rowData.FORM_NO,
      "schd_name": this.rowData.SCHD_NAME,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.f1120lSchAService.fetchSchdFormView(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject && response.jsonObject.length > 0) {
          this.scheduleAFormView.rows = [];
          for (let i = 0; i < response.jsonObject.length; i++) {
            const tempDataObj: any = {};
            tempDataObj.num = response.jsonObject[i].LINE_NO;
            tempDataObj.description = response.jsonObject[i].LINE_DESCRIPTION;
            tempDataObj.value1 = response.jsonObject[i].DIV_AND_INC_A;
            tempDataObj.percent = response.jsonObject[i].PERCENT_B;
            tempDataObj.value2 = response.jsonObject[i].DED_A_TIMES_B;
            this.scheduleAFormView.rows.push(tempDataObj);
          }
          this.showFormLoader = false;
        } else {
          this.showFormLoader = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching schedule form view:', error);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  getAccts(type: string, state: boolean, is_pt: number, acct: any, index: number, line_no: any, initCall: number = 0) {
    this.no_main_sub_data = false;
    this.no_total_lines = false;
    this.currType = type;

    if (is_pt == 1) {
      this.main_indx = "init";
      this.main_sub_indx = "init";
      this.acct_indx = "init";
    }

    if (this.main_indx === "init" && type === "main") {
      this.main_indx = index;
    } else if (this.main_sub_indx === "init" && type === "main_sub") {
      this.main_sub_indx = index;
    } else if (this.acct_indx === "init" && type === "accounts") {
      this.acct_indx = index;
    }

    if (this.currType === 'main') {
      if (!!this.formData[index].main_reload) {
        this.formData[index].toggleTotal = !state;
        this.formData[index].main_loader = true;
        this.formData[index].main_reload = 0;
        if (this.formData.length > 1 && this.main_indx !== index) {
          this.formData[this.main_indx].toggleTotal = false;
          this.formData[this.main_indx].main_reload = 1;
          this.totalLines = [];
          this.main_sub_data = [];
          this.acct_data = [];
          this.main_indx = index;
        }
      } else {
        this.formData[index].toggleTotal = !state;
        return;
      }
    } else if (this.currType === 'main_sub') {
      if (!!this.totalLines[index].main_sub_reload) {
        this.totalLines[index].toggle = !state;
        this.totalLines[index].main_sub_loader = true;
        this.totalLines[index].main_sub_reload = 0;
        if (this.totalLines.length > 1 && this.main_sub_indx !== index) {
          this.totalLines[this.main_sub_indx].toggle = false;
          this.totalLines[this.main_sub_indx].main_sub_reload = 1;
          this.main_sub_data = [];
          this.acct_data = [];
          this.main_sub_indx = index;
        }
      } else {
        this.totalLines[index].toggle = !state;
        return;
      }
    } else if (this.currType === 'accounts') {
      if (!!this.main_sub_data[index].child_reload) {
        this.main_sub_data[index].toggle = !state;
        this.main_sub_data[index].child_loader = true;
        this.main_sub_data[index].child_reload = 0;
        if (this.main_sub_data.length > 1 && this.acct_indx !== index) {
          this.main_sub_data[this.acct_indx].toggle = false;
          this.main_sub_data[this.acct_indx].child_reload = 1;
          this.acct_data = [];
          this.acct_indx = index;
        }
      } else {
        this.main_sub_data[index].toggle = !state;
        return;
      }
    } else if (this.currType === 'main_acct') {
      if (!!this.accountData[index].main_reload) {
        this.accountData[index].toggle = !state;
        this.accountData[index].main_loader = true;
        this.accountData[index].main_reload = 0;
      } else {
        this.accountData[index].toggle = !state;
        return;
      }
    }

    const params = {
      "action_id": 30098,
      "tax_year": this.rowData.TAX_YEAR,
      "screen_key": this.screen_key,
      "acct_key": acct,
      "combination_key": this.rowData.COMBINATION_KEY,
      "is_parent": is_pt,
      "line_no": line_no
    };

    this.f1120lSchAService.getAccts(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          if (this.currType == "parent" && response.jsonObject.length > 1) {
            if (response.jsonObject[1].TYPE == "account") {
              this.showDiagLoader = false;
              this.formData.push(response.jsonObject[0]);
              this.formData[0].toggleTotal = false;
              this.formData[0].main_reload = 1;
              this.accountData.push(response.jsonObject[1]);
              this.accountData[0].toggle = false;
              this.accountData[0].main_reload = 1;
              this.accountData[0].main_loader = false;
            } else {
              this.createMapping(this.currType, index, response.jsonObject);
            }
          } else {
            this.createMapping(this.currType, index, response.jsonObject);
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error getting accounts:', error);
        if (this.currType === 'parent') {
          this.showDiagLoader = false;
        }
        this.cdr.detectChanges();
      }
    );
  }

  createMapping(currType: string, index: number, data: any[]) {
    if (currType === 'parent') {
      this.formData = data;
      this.formData.forEach((value, key) => {
        this.formData[key].toggleTotal = false;
        this.formData[key].main_reload = 1;
        this.formData[key].main_loader = false;
      });
      this.showDiagLoader = false;
    } else if (currType === "main") {
      if (data.length > 0) {
        this.formData[index].main_loader = false;
        this.totalLines = data;
        this.totalLines.forEach((value, key) => {
          this.totalLines[key].toggle = false;
          this.totalLines[key].main_sub_reload = 1;
          this.totalLines[key].main_sub_loader = false;
        });
        if (this.totalLines[0].TYPE === "sys-form") {
          this.show_toggle_total = false;
        }
      } else {
        this.formData[index].main_loader = false;
        this.no_total_lines = true;
      }
    } else if (currType === "main_sub") {
      if (data.length > 0) {
        this.main_sub_data = data;
        this.main_sub_data.forEach((value, key) => {
          this.main_sub_data[key].toggle = false;
          this.main_sub_data[key].child_reload = 1;
          this.main_sub_data[key].child_loader = false;
        });
        if (this.main_sub_data[0].TYPE === "sys-form") {
          this.show_toggle = false;
        }
        this.totalLines[index].main_sub_loader = false;
      } else {
        this.no_main_sub_data = true;
        this.totalLines[index].main_sub_loader = false;
      }
    } else if (currType === "main_acct") {
      if (data.length > 0) {
        this.acctChild = data;
        this.accountData[0].main_loader = false;
      } else {
        this.accountData[0].main_loader = false;
        this.no_acct_child = true;
      }
    } else {
      this.acct_data = data;
      this.main_sub_data[index].child_loader = false;
    }
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  // Number formatting pipe function
  numberWithCommas(x: any): string {
    if (x === null || x === undefined || x === '') {
      return '';
    }
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
}

